import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building, User, Phone, Mail, MessageSquare, Linkedin,
  Calendar, Bot, Trash2, AlertTriangle, Save, Sparkles,
  Eye, FileText, ExternalLink, Globe, Users, Pause, Play, ChevronDown, Check
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { usePipelineDeals, EnrichedDeal } from '../../hooks/usePipelineDeals';
import { usePipelineStagesConfig } from '../../hooks/usePipelineStagesConfig';
import { useTimezone } from '../../hooks/useTimezone';
import { toLocaleDateStringInTz, formatInTz } from '../../utils/dateFormatters';
import LoadingScreen from '../shared/LoadingScreen';
import InsightsModal from '../LeadGen/InsightsModal';
import OverviewModal from '../LeadGen/OverviewModal';
import DealActivityTimeline from './DealActivityTimeline';

const DealViewPage: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const tz = useTimezone();
  const { data: allDeals, isLoading: dealsLoading, updateDeal, deleteDeal } = usePipelineDeals();
  const { stages } = usePipelineStagesConfig();

  const [notes, setNotes] = useState('');
  const [dealValue, setDealValue] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const stageDropdownRef = useRef<HTMLDivElement>(null);
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);
  const [isSavingValue, setIsSavingValue] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [showOverviewModal, setShowOverviewModal] = useState(false);

  const deal: EnrichedDeal | undefined = useMemo(() => {
    return allDeals.find(d => d.deal_id === dealId);
  }, [allDeals, dealId]);

  useEffect(() => {
    if (deal) {
      setNotes(deal.notes || '');
      setDealValue(deal.deal_value != null ? String(deal.deal_value) : '');
      setSelectedStage(deal.pipeline_stage || '');
    }
  }, [deal]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (stageDropdownRef.current && !stageDropdownRef.current.contains(e.target as Node)) {
        setStageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStageChange = async (newStage: string) => {
    if (!deal || newStage === selectedStage) return;
    setIsUpdatingStage(true);
    try {
      await updateDeal(deal.deal_id, { pipeline_stage: newStage });
      setSelectedStage(newStage);
    } finally {
      setIsUpdatingStage(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!deal) return;
    setIsUpdatingNotes(true);
    try {
      await updateDeal(deal.deal_id, { notes });
    } finally {
      setIsUpdatingNotes(false);
    }
  };

  const handleSaveDealValue = async () => {
    if (!deal) return;
    setIsSavingValue(true);
    try {
      const numericValue = dealValue ? parseFloat(dealValue) : null;
      await updateDeal(deal.deal_id, { deal_value: numericValue });
    } finally {
      setIsSavingValue(false);
    }
  };

  const handleDeleteDeal = async () => {
    if (!deal) return;
    setIsDeleting(true);
    try {
      await deleteDeal(deal.deal_id);
      navigate('/pipeline');
    } catch {
      setIsDeleting(false);
    }
  };

  const formatMeetingTime = (timeStr: string) => {
    if (!timeStr || timeStr === 'N/A') return 'N/A';
    try {
      const date = new Date(`1970-01-01T${timeStr}`);
      if (isNaN(date.getTime())) return timeStr;
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return minutes === 0 ? `${hours}${ampm}` : `${hours}:${String(minutes).padStart(2, '0')}${ampm}`;
    } catch { return timeStr; }
  };

  if (dealsLoading && allDeals.length === 0) return <LoadingScreen />;

  if (!deal) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/pipeline')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
          <ArrowLeft size={20} className="mr-1" />
          <span className="font-medium">Pipeline</span>
        </button>
        <div className="text-center py-20">
          <div className="text-gray-400 text-lg font-medium">Deal not found</div>
          <p className="text-gray-500 text-sm mt-2">This deal may have been deleted or you don't have access.</p>
        </div>
      </div>
    );
  }

  const stageObj = stages.find(s => s.name === selectedStage);

  return (
    <div className="space-y-6">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/pipeline')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors group">
          <ArrowLeft size={20} className="mr-1.5 group-hover:-translate-x-0.5 transition-transform" />
          <span className="font-semibold text-sm">Pipeline</span>
        </button>
        <div className="flex items-center gap-3">
          {deal.ovrall_lead_status && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-[#1875FF]/10 text-[#1875FF] border border-[#1875FF]/30">
              {deal.ovrall_lead_status}
            </span>
          )}
          {deal.ovrall_lead_temp && (
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${
              deal.ovrall_lead_temp.toLowerCase() === 'hot' ? 'bg-red-50 text-red-600 border-red-200' :
              deal.ovrall_lead_temp.toLowerCase() === 'warm' ? 'bg-orange-50 text-orange-600 border-orange-200' :
              'bg-blue-50 text-blue-600 border-blue-200'
            }`}>
              {deal.ovrall_lead_temp}
            </span>
          )}
        </div>
      </div>

      {/* Main two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Activity */}
        <div className="lg:col-span-2 space-y-5">
          {/* AI Summary */}
          {deal.ovrall_convo_summary && (
            <div className="bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 border border-[#1875FF]/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-lg flex items-center justify-center">
                  <Sparkles size={14} className="text-white" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">AI Summary</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{deal.ovrall_convo_summary}</p>
            </div>
          )}

          {/* Activity Timeline */}
          <DealActivityTimeline
            leadId={deal.lead_id}
            leadName={deal.dm_full_name || 'Lead'}
            companyName={deal.company_name || undefined}
            agentName={undefined}
            dealNotes={notes}
            onNotesChange={setNotes}
            onSaveNotes={handleSaveNotes}
            isSavingNotes={isUpdatingNotes}
          />
        </div>

        {/* Right column - Sidebar */}
        <div className="space-y-5">
          {/* Company Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{deal.company_name || 'Unknown Company'}</h2>
                {deal.company_website && (
                  <a
                    href={deal.company_website.startsWith('http') ? deal.company_website : `https://${deal.company_website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#1875FF] hover:text-[#1875FF]/80 flex items-center gap-1 mt-1"
                  >
                    <Globe size={11} />
                    {deal.company_website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </div>
              {deal.company_linkedin && (
                <a
                  href={deal.company_linkedin.startsWith('http') ? deal.company_linkedin : `https://${deal.company_linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0A66C2] hover:text-[#0A66C2]/80"
                >
                  <Linkedin size={18} />
                </a>
              )}
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Industry</span>
                <span className="font-medium text-gray-900">{deal.industry_1 || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Employees</span>
                <span className="font-medium text-gray-900">{deal.employee_headcount || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Country</span>
                <span className="font-medium text-gray-900">{deal.company_country || 'N/A'}</span>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Stage</span>
                <div ref={stageDropdownRef} className="relative">
                  <button
                    onClick={() => !isUpdatingStage && setStageDropdownOpen(!stageDropdownOpen)}
                    disabled={isUpdatingStage}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg bg-white hover:border-[#1875FF]/40 hover:shadow-sm transition-all cursor-pointer disabled:opacity-50"
                  >
                    {isUpdatingStage ? (
                      <div className="w-3 h-3 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin" />
                    ) : null}
                    <span className="truncate max-w-[120px]">{selectedStage || 'Select'}</span>
                    <ChevronDown size={12} className={`text-gray-400 transition-transform ${stageDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {stageDropdownOpen && (
                    <div className="absolute right-0 z-50 mt-1.5 w-44 bg-white border-2 border-[#1875FF]/20 rounded-xl shadow-2xl shadow-[#1875FF]/10 overflow-hidden animate-fadeIn">
                      <div className="py-1 max-h-[200px] overflow-y-auto">
                        {stages.map((s, idx) => {
                          const isActive = s.name === selectedStage;
                          return (
                            <button
                              key={s.name}
                              onClick={() => {
                                handleStageChange(s.name);
                                setStageDropdownOpen(false);
                              }}
                              className={`w-full px-3.5 py-2 text-left flex items-center justify-between text-xs font-medium transition-all duration-150 ${idx > 0 ? 'border-t border-gray-50' : ''} ${isActive ? 'bg-[#1875FF]/5 text-[#1875FF]' : 'text-gray-700 hover:bg-gray-50'}`}
                            >
                              <span>{s.name}</span>
                              {isActive && <Check size={12} className="text-[#1875FF] flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Deal Value</span>
                <div className="relative w-28">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                  <input
                    type="number"
                    value={dealValue}
                    onChange={e => setDealValue(e.target.value)}
                    onBlur={handleSaveDealValue}
                    placeholder="0"
                    className="w-full pl-6 pr-2.5 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-[#1875FF] focus:border-[#1875FF] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>
              {deal.channel_booked && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Booked via</span>
                  <span className="font-medium text-gray-900 capitalize">{deal.channel_booked}</span>
                </div>
              )}
            </div>
          </div>

          {/* Contacts Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Contacts</h3>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {(deal.dm_full_name || 'U')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-900 truncate">{deal.dm_full_name || 'Unknown'}</span>
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-[#1875FF]/10 text-[#1875FF] rounded uppercase">Primary</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{deal.dm_title || 'No title'}</p>
                <div className="flex items-center gap-2 mt-2">
                  {deal.dm_mobile_1 && (
                    <a href={`tel:${deal.dm_mobile_1}`} className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-[#1875FF]/10 flex items-center justify-center transition-colors" title={deal.dm_mobile_1}>
                      <Phone size={13} className="text-gray-600" />
                    </a>
                  )}
                  {deal.dm_email_1 && (
                    <a href={`mailto:${deal.dm_email_1}`} className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-[#1875FF]/10 flex items-center justify-center transition-colors" title={deal.dm_email_1}>
                      <Mail size={13} className="text-gray-600" />
                    </a>
                  )}
                  {deal.dm_linkedin && (
                    <a href={deal.dm_linkedin.startsWith('http') ? deal.dm_linkedin : `https://${deal.dm_linkedin}`} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-[#1875FF]/10 flex items-center justify-center transition-colors" title="LinkedIn">
                      <Linkedin size={13} className="text-gray-600" />
                    </a>
                  )}
                  {deal.dm_mobile_1 && (
                    <span className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center" title="WhatsApp">
                      <MessageSquare size={13} className="text-gray-600" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sales Person */}
          {deal.sales_person && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Sales Person Assigned</h3>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {deal.sales_person[0].toUpperCase()}
                </div>
                <div>
                  <span className="text-sm font-bold text-gray-900">{deal.sales_person}</span>
                </div>
              </div>
            </div>
          )}

          {/* Meeting Details */}
          {(deal.meeting_date || deal.meeting_status) && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Meeting</h3>
              <div className="space-y-2 text-sm">
                {deal.meeting_status && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className="font-medium text-gray-900">{deal.meeting_status}</span>
                  </div>
                )}
                {deal.meeting_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date</span>
                    <span className="font-medium text-gray-900">{toLocaleDateStringInTz(deal.meeting_date, tz)}</span>
                  </div>
                )}
                {deal.meeting_time && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Time</span>
                    <span className="font-medium text-gray-900">{formatMeetingTime(deal.meeting_time)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Actions</h3>

            <button
              onClick={() => setShowInsightsModal(true)}
              className="flex items-center gap-2 w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-[#1875FF]/5 hover:text-[#1875FF] border border-gray-200 hover:border-[#1875FF]/30 transition-all"
            >
              <Eye size={15} />
              View Insights
            </button>

            <button
              onClick={() => setShowOverviewModal(true)}
              className="flex items-center gap-2 w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-[#05E0D1]/5 hover:text-[#0bb5a5] border border-gray-200 hover:border-[#05E0D1]/30 transition-all"
            >
              <FileText size={15} />
              Outreach Overview
            </button>

            <button
              onClick={() => navigate(`/sales-coach?action=deal-advice&dealId=${deal.deal_id}&leadId=${deal.lead_id}`)}
              className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] hover:shadow-lg hover:scale-[1.01] transition-all"
            >
              <Bot size={15} />
              Get AI Deal Advice
            </button>

            <div className="border-t border-gray-100 pt-3 mt-3">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-red-500 bg-red-50 border border-red-200 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all"
                >
                  <Trash2 size={14} />
                  Delete This Deal
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                    <AlertTriangle size={14} className="flex-shrink-0" />
                    <span className="text-xs font-medium">Are you sure? This cannot be undone.</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteDeal}
                      disabled={isDeleting}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold text-white bg-red-500 hover:bg-red-600 transition-all disabled:opacity-50"
                    >
                      {isDeleting ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Trash2 size={12} />}
                      {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showInsightsModal && (
        <InsightsModal lead={deal} onClose={() => setShowInsightsModal(false)} />
      )}
      {showOverviewModal && (
        <OverviewModal lead={deal} onClose={() => setShowOverviewModal(false)} />
      )}
    </div>
  );
};

export default DealViewPage;
